import { useEffect, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import itLocale from "@fullcalendar/core/locales/it";
import { usePhone } from "@/hooks/useMedia";

const TIME = { hour: "2-digit", minute: "2-digit", hour12: false };
const HOUR = 3600000;

const phoneViews = (views) => {
  const list = views.split(",");
  const agenda = list.includes("listMonth") && !list.includes("timeGridWeek") ? "listMonth" : "listWeek";
  return [agenda, list.includes("timeGridDay") ? "timeGridDay" : null, "dayGridMonth"].filter(Boolean);
};

export const CoreCalendar = ({ events, onSelect, onEventClick, initialView = "dayGridMonth", editable = false, onEventDrop, onEventResize, eventContent,
  views = "dayGridMonth,timeGridWeek,timeGridDay,listMonth", focusDate, onRangeChange }) => {
  const ref = useRef(null);
  const touch = useRef(null);
  const phone = usePhone();
  const pv = phoneViews(views);
  useEffect(() => { if (focusDate) ref.current?.getApi().gotoDate(focusDate); }, [focusDate]);
  useEffect(() => {
    const api = ref.current?.getApi();
    if (!api) return;
    const target = phone ? pv[0] : initialView;
    if (api.view.type !== target) api.changeView(target);
  }, [phone]); // eslint-disable-line react-hooks/exhaustive-deps
  const tapCreate = (info) => {
    if (!onSelect) return;
    const start = info.date;
    const end = new Date(start.getTime() + (info.allDay ? 24 * HOUR : HOUR));
    onSelect({ start, end, allDay: info.allDay, startStr: info.dateStr, endStr: end.toISOString() });
  };
  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    const s = touch.current;
    touch.current = null;
    if (!phone || !s) return;
    const dx = e.changedTouches[0].clientX - s.x, dy = e.changedTouches[0].clientY - s.y;
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      const api = ref.current?.getApi();
      dx < 0 ? api?.next() : api?.prev();
    }
  };
  return (
    <div className={`core-fc ${phone ? "core-fc-phone" : ""}`} data-testid="core-calendar" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <FullCalendar
        ref={ref}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        locale={itLocale}
        firstDay={1}
        initialView={phone ? pv[0] : initialView}
        headerToolbar={phone ? { left: "prev", center: "title", right: "next" } : { left: "prev,next today", center: "title", right: views }}
        footerToolbar={phone ? { left: "today", right: pv.join(",") } : false}
        buttonText={{ today: "Oggi", month: "Mese", week: "Settimana", day: "Giorno", list: "Elenco" }}
        views={{ listWeek: { buttonText: "Agenda" }, listMonth: { buttonText: "Elenco" } }}
        noEventsContent="Nessun elemento in questo periodo"
        allDayText="Tutto il giorno"
        selectable={!phone}
        selectMirror
        select={(info) => { onSelect?.(info); info.view.calendar.unselect(); }}
        dateClick={phone ? tapCreate : undefined}
        eventClick={(info) => { info.jsEvent.preventDefault(); onEventClick?.(info.event); }}
        editable={editable && !phone}
        eventDurationEditable={!!onEventResize && !phone}
        eventDrop={onEventDrop}
        eventResize={onEventResize}
        eventContent={eventContent}
        events={events}
        datesSet={(info) => onRangeChange?.({ da: info.start.toISOString(), a: info.end.toISOString() })}
        slotMinTime="07:00:00"
        slotMaxTime="21:00:00"
        scrollTime="08:00:00"
        nowIndicator
        height={phone ? "auto" : 760}
        dayMaxEvents={phone ? 2 : 4}
        eventTimeFormat={TIME}
        slotLabelFormat={TIME}
        eventDisplay="block"
      />
    </div>
  );
};
